import { useId, useState, type FormEvent } from 'react';
import { useCurrentUser } from '@/features/auth/api';
import { can } from '@/lib/permissions';
import { useDebounced } from '@/lib/useDebounced';
import { Combobox } from '@/components/ui/Combobox';

import type { Meeting, MeetingPlatform, MeetingStatus, MeetingType } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { EditorColumns, EditorPage } from '@/components/layout/EditorPage';
import { Panel } from '@/components/layout/Page';
import { useUnsavedChanges } from '@/lib/useUnsavedChanges';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useCandidates } from '@/features/candidates/api';
import { EMPTY_FILTERS } from '@/features/candidates/urlState';

import { useCreateMeeting, useDeleteMeeting, useUpdateMeeting } from '../api';
import {
  DURATION_OPTIONS,
  MEETING_STATUS_OPTIONS,
  MEETING_TYPE_OPTIONS,
  PLATFORM_OPTIONS,
} from '../labels';
import styles from './ScheduleDrawer.module.css';

/** An instant, as the two form controls hold it. */
function splitInstant(at: Date): { date: string; time: string } {
  const pad = (value: number) => String(value).padStart(2, '0');
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
    time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
  };
}

/**
 * The two fields back into one moment.
 *
 * `new Date(y, m, d, h, min)` builds it in the browser's own timezone, which is the one
 * the person typing is in: they mean 3pm where they are. `toISOString` then carries the
 * offset to the server, and the row records the instant rather than the wall clock.
 */
function joinInstant(date: string, time: string): Date | null {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  if (!year || !month || !day || hour === undefined || minute === undefined) return null;
  return new Date(year, month - 1, day, hour, minute);
}

/**
 * Schedule a meeting, or change one.
 *
 * One form for both, because the fields are identical and the only difference is
 * whether an id exists; two components would drift apart on the first field added.
 */
export function MeetingForm({
  backTo = '/meetings/calendar',
  meeting,
  initialDate,
  candidateId,
  candidateName,
  onClose,
}: {
  /** The meeting being edited, or null to schedule a new one. */
  meeting: Meeting | null;
  /** Where the editor was opened from on the grid, so the date starts sensible. */
  initialDate?: Date | undefined;
  /** Fixed when opened from a candidate's record. */
  candidateId?: string | undefined;
  candidateName?: string | undefined;
  backTo?: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const formId = useId();
  const { data: user } = useCurrentUser();
  const canSave = can(user, 'meetings', meeting ? 'edit' : 'create');
  const canDelete = can(user, 'meetings', 'delete');
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | null>(
    null,
  );
  const debouncedSearch = useDebounced(search, 250);
  const create = useCreateMeeting();
  const update = useUpdateMeeting();
  const remove = useDeleteMeeting();

  const [parts] = useState(() =>
    splitInstant(meeting ? new Date(meeting.scheduled_at) : (initialDate ?? new Date())),
  );

  const [title, setTitle] = useState(meeting?.title ?? '');
  const [candidate, setCandidate] = useState(meeting?.candidate.id ?? candidateId ?? '');
  const [type, setType] = useState<MeetingType>(meeting?.meeting_type ?? 'discovery_call');
  const [date, setDate] = useState(parts.date);
  const [time, setTime] = useState(parts.time);
  const [duration, setDuration] = useState(String(meeting?.duration_minutes ?? 30));
  const [platform, setPlatform] = useState<MeetingPlatform>(meeting?.platform ?? 'zoom');
  const [joinUrl, setJoinUrl] = useState(meeting?.join_url ?? '');
  const [status, setStatus] = useState<MeetingStatus>(meeting?.status ?? 'scheduled');
  const [problem, setProblem] = useState<string | null>(null);

  const candidates = useCandidates(
    { ...EMPTY_FILTERS, search: debouncedSearch, per_page: 50 },
    !meeting && !candidateId && can(user, 'candidates'),
  );
  const candidateOptions = (candidates.data?.items ?? []).map((item) => ({
    value: item.id,
    label: `${item.full_name} · ${item.reference}`,
  }));
  if (
    selectedOption &&
    !candidateOptions.some((option) => option.value === selectedOption.value)
  ) {
    candidateOptions.unshift(selectedOption);
  }

  const busy = create.isPending || update.isPending || remove.isPending;
  const dirty =
    title !== (meeting?.title ?? '') ||
    candidate !== (meeting?.candidate.id ?? candidateId ?? '') ||
    type !== (meeting?.meeting_type ?? 'discovery_call') ||
    date !== parts.date ||
    time !== parts.time ||
    duration !== String(meeting?.duration_minutes ?? 30) ||
    platform !== (meeting?.platform ?? 'zoom') ||
    joinUrl !== (meeting?.join_url ?? '') ||
    status !== (meeting?.status ?? 'scheduled');
  const unsaved = useUnsavedChanges(dirty || busy);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSave || busy) return;
    setProblem(null);

    const at = joinInstant(date, time);
    if (!at || !Number.isFinite(at.getTime())) {
      setProblem('Give the meeting a date and a time.');
      return;
    }
    if (!candidate) {
      setProblem('Choose which candidate this meeting is about.');
      return;
    }

    try {
      if (meeting) {
        await update.mutateAsync({
          id: meeting.id,
          changes: {
            title,
            meeting_type: type,
            scheduled_at: at.toISOString(),
            duration_minutes: Number(duration),
            platform,
            join_url: joinUrl.trim() || null,
            status,
          },
        });
        toast.success('Meeting updated.');
      } else {
        await create.mutateAsync({
          candidate_id: candidate,
          title,
          meeting_type: type,
          scheduled_at: at.toISOString(),
          duration_minutes: Number(duration),
          platform,
          join_url: joinUrl.trim() || null,
        });
        toast.success('Meeting scheduled.');
      }
      unsaved.allowSavedExit();
      onClose();
    } catch (error) {
      // Beside the fields, because a toast would take the message
      // away while somebody is still looking for what to fix.
      setProblem(
        error instanceof ApiError ? error.message : 'That meeting could not be saved.',
      );
    }
  };

  const drop = async () => {
    if (!meeting || !canDelete) return;
    try {
      await remove.mutateAsync(meeting.id);
      toast.success('Meeting removed.');
      unsaved.allowSavedExit();
      onClose();
    } catch (error) {
      setProblem(
        error instanceof ApiError ? error.message : 'That meeting could not be removed.',
      );
    }
  };

  return (
    <EditorPage
      backTo={backTo}
      backLabel={backTo.startsWith('/candidates/') ? 'Back to candidate' : 'Back to Calendar'}
      unsaved={unsaved}
      busy={busy}
      title={meeting ? (canSave ? 'Edit meeting' : 'Meeting details') : 'Schedule a meeting'}
      description={
        meeting
          ? 'Times are shown in your own timezone.'
          : 'Times are in your own timezone. Everyone sees the moment in theirs.'
      }
      actions={
        <>
          {meeting && canDelete ? (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                void drop();
              }}
            >
              Remove
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {canSave ? (
            <Button type="submit" form={formId} loading={busy}>
              {meeting ? 'Save' : 'Schedule'}
            </Button>
          ) : null}
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void submit(event)}>
        {problem ? (
          <Alert tone="danger">
            <b>{problem}</b>
          </Alert>
        ) : null}

        <fieldset className={styles.form} disabled={!canSave || busy}>
          <EditorColumns
            settings={
              <Panel title="Schedule">
                {' '}
                <div className={styles.pair}>
                  <Field label="Date" required>
                    {({ id }) => (
                      <Input
                        id={id}
                        type="date"
                        required
                        value={date}
                        onChange={(event) => {
                          setDate(event.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <Field label="Time" required>
                    {({ id }) => (
                      <Input
                        id={id}
                        type="time"
                        required
                        value={time}
                        onChange={(event) => {
                          setTime(event.target.value);
                        }}
                      />
                    )}
                  </Field>
                </div>
                <div className={styles.pair}>
                  <Field label="Duration">
                    {({ id }) => (
                      <Select
                        id={id}
                        value={duration}
                        onChange={(event) => {
                          setDuration(event.target.value);
                        }}
                        options={DURATION_OPTIONS}
                      />
                    )}
                  </Field>
                  <Field label="Platform">
                    {({ id }) => (
                      <Select
                        id={id}
                        value={platform}
                        onChange={(event) => {
                          setPlatform(event.target.value as MeetingPlatform);
                        }}
                        options={PLATFORM_OPTIONS}
                      />
                    )}
                  </Field>
                </div>
                {meeting ? (
                  <Field label="Status">
                    {({ id }) => (
                      <Select
                        id={id}
                        value={status}
                        onChange={(event) => {
                          setStatus(event.target.value as MeetingStatus);
                        }}
                        options={MEETING_STATUS_OPTIONS.filter(
                          (option) =>
                            (option.value !== 'completed' && option.value !== 'no_show') ||
                            (joinInstant(date, time)?.getTime() ?? Infinity) +
                              Number(duration) * 60_000 <=
                              Date.now(),
                        )}
                      />
                    )}
                  </Field>
                ) : null}
              </Panel>
            }
          >
            <Panel title="Meeting details">
              {' '}
              <Field label="Title" required>
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    describedBy={describedBy}
                    value={title}
                    placeholder="Discovery call"
                    required
                    maxLength={200}
                    onChange={(event) => {
                      setTitle(event.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label="Candidate" required>
                {({ id, describedBy }) =>
                  meeting || candidateId ? (
                    <Input
                      id={id}
                      describedBy={describedBy}
                      readOnly
                      value={meeting?.candidate.full_name ?? candidateName ?? candidateId ?? ''}
                    />
                  ) : (
                    <Combobox
                      id={id}
                      describedBy={describedBy}
                      value={candidate}
                      disabled={!can(user, 'candidates')}
                      onSearch={setSearch}
                      onChange={(value) => {
                        setCandidate(value);
                        setSelectedOption(
                          candidateOptions.find((option) => option.value === value) ?? null,
                        );
                      }}
                      placeholder="Search candidates by name, email or reference"
                      options={candidateOptions}
                    />
                  )
                }
              </Field>
              {!meeting && !candidateId && !can(user, 'candidates') ? (
                <Alert tone="warning">
                  Candidate viewing permission is needed to choose a candidate.
                </Alert>
              ) : null}
              {candidates.isError ? (
                <Alert tone="danger">Candidates could not be loaded.</Alert>
              ) : null}
              <Field label="Type">
                {({ id }) => (
                  <Select
                    id={id}
                    value={type}
                    onChange={(event) => {
                      setType(event.target.value as MeetingType);
                    }}
                    options={MEETING_TYPE_OPTIONS}
                  />
                )}
              </Field>
              <Field label="Joining link" hint="Pasted from Zoom, Meet or whatever was used.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    describedBy={describedBy}
                    type="url"
                    value={joinUrl}
                    placeholder="https://"
                    onChange={(event) => {
                      setJoinUrl(event.target.value);
                    }}
                  />
                )}
              </Field>
            </Panel>
          </EditorColumns>
        </fieldset>
      </form>
    </EditorPage>
  );
}
